import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../common/breadcrumb/Breadcrumb';
import NiceSelect from '../common/NiceSelect';
import Loader from '../preloander/Loander';
import useScrollToTop from '../../hooks/UseScrollToTop';
import useUserLocation from '../../hooks/useUserLocation';
import { FaLocationDot, FaRegClock } from "react-icons/fa6";

const ALL_VALUE = "";

// NiceSelect keeps its own `current` state, seeded once from
// options[defaultCurrent] on mount - so the "All ..." placeholder must
// already be in the options array on the very first render (index 0),
// before the async filter-options/category fetch resolves. The rest of
// the list is still fully reactive to prop changes, so real values
// appended once the fetch completes show up correctly either way.
const buildOptions = (rawList, allLabel) => {
    const options = [{ id: ALL_VALUE, optionName: allLabel }];
    (rawList || []).forEach(item => {
        if (item && typeof item === 'object') {
            options.push({ id: item.value, optionName: item.label });
        } else {
            options.push({ id: String(item), optionName: String(item) });
        }
    });
    return options;
};

const EMPTY_FILTERS = {
    category: "", genre: "", country: "", city: "", venue: "", organizer: "", month: ""
};

// Picks the soonest not-yet-past schedule entry (falls back to the
// earliest entry overall if every date has technically already passed -
// shouldn't happen given the backend only returns future events, but
// keeps this safe either way) so a multi-day event's card shows the
// date/time a visitor would actually care about, not just whichever
// entry happened to be first in the array.
const getNextScheduleEntry = (schedule) => {
    if (!Array.isArray(schedule) || schedule.length === 0) return null;
    const todayStart = new Date(new Date().toDateString());
    const upcoming = schedule.filter(entry => entry?.eventDate && new Date(entry.eventDate) >= todayStart);
    const pool = upcoming.length > 0 ? upcoming : schedule;
    return pool.reduce((earliest, entry) => {
        if (!entry?.eventDate) return earliest;
        if (!earliest) return entry;
        return new Date(entry.eventDate) < new Date(earliest.eventDate) ? entry : earliest;
    }, null);
};

const formatEventDateTime = (rawSchedule, locale) => {
    let schedule;
    try {
        schedule = JSON.parse(rawSchedule);
    } catch {
        return "";
    }
    const nextEntry = getNextScheduleEntry(schedule);
    if (!nextEntry?.eventDate) return "";

    const date = new Date(nextEntry.eventDate);
    const datePart = isNaN(date) ? "" : date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    const timePart = nextEntry.eventStartTime || "";

    return [datePart, timePart].filter(Boolean).join(' · ');
};

const EventsListing = () => {
    useScrollToTop();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { eventsListing } = t("screen");
    const { latitude, longitude, isLocationReady } = useUserLocation();

    const [pageLoading, setPageLoading] = useState(true);
    const [dataLoading, setDataLoading] = useState(true);

    const [categories, setCategories] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        countries: [], cities: [], genres: [], venues: [], organizers: [], months: []
    });

    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const [events, setEvents] = useState([]);
    const [isFallback, setIsFallback] = useState(false);

    useEffect(() => {
        setTimeout(() => setPageLoading(false), 1000);
    }, []);

    useEffect(() => {
        const fetchStaticData = async () => {
            try {
                const [categoryRes, filterOptionsRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/api/public/listAllCategory`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/public/getEventFilterOptions`),
                ]);
                setCategories(categoryRes?.data?.category || []);
                setFilterOptions({
                    countries: filterOptionsRes?.data?.countries || [],
                    cities: filterOptionsRes?.data?.cities || [],
                    genres: filterOptionsRes?.data?.genres || [],
                    venues: filterOptionsRes?.data?.venues || [],
                    organizers: filterOptionsRes?.data?.organizers || [],
                    months: filterOptionsRes?.data?.months || [],
                });
            } catch (error) {
                console.error("Error fetching event filter data:", error);
            }
        };
        fetchStaticData();
    }, []);

    useEffect(() => {
        // Wait for the geolocation prompt to resolve one way or another
        // (granted, denied, unsupported, or timed out) before firing the
        // first request, same reasoning as the homepage sections - avoids
        // fetching once unscoped and again right after location arrives.
        if (!isLocationReady) return;

        const fetchEvents = async () => {
            setDataLoading(true);
            try {
                const params = Object.fromEntries(
                    Object.entries(filters).filter(([, value]) => value)
                );
                // Only meaningful when no filters are active - the backend
                // ignores lat/long entirely once real filters are set, so
                // sending it unconditionally here is harmless.
                if (latitude && longitude) {
                    params.latitude = latitude;
                    params.longitude = longitude;
                }
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/public/getFilteredEvents`, { params });
                setEvents(response?.data?.events || []);
                setIsFallback(!!response?.data?.isFallback);
            } catch (error) {
                console.error("Error fetching filtered events:", error);
                setEvents([]);
                setIsFallback(false);
            } finally {
                setDataLoading(false);
            }
        };
        fetchEvents();
    }, [filters, isLocationReady, latitude, longitude]);

    const handleFilterChange = (item, name) => {
        setFilters(prev => ({ ...prev, [name]: item?.id || "" }));
    };

    const handleClearFilters = () => {
        setFilters(EMPTY_FILTERS);
    };

    const handleEventClick = (item) => {
        navigate("/event-details", { state: { id: item._id } });
    };

    const monthOptions = (filterOptions.months || []).map(m => ({
        value: String(m),
        label: new Date(2000, m - 1, 1).toLocaleString(i18n.language === 'It' ? 'it-IT' : 'en-US', { month: 'long' }),
    }));

    // filterOptions.countries comes back as raw ISO codes (e.g. "IT") -
    // show the localized country name instead of the bare code.
    const countryOptions = (filterOptions.countries || []).map(code => {
        let label = code;
        try {
            label = new Intl.DisplayNames([i18n.language === 'It' ? 'it' : 'en'], { type: 'region' }).of(code) || code;
        } catch {
            label = code;
        }
        return { value: code, label };
    });

    // Only used by the Category filter, currently commented out below.
    // const categoryOptions = categories.map(c => ({ value: c._id, label: c.category }));

    const hasActiveFilters = Object.values(filters).some(Boolean);

    return (
        <>
            {pageLoading ? <Loader /> : (
                <div>
                    <Breadcrumb title={eventsListing?.title} />

                    <section className="pt-100 pb-100">
                        <div className="container">

                            <div className="ms-events-filter-bar mb-40">
                                {/* Category filter - commented out (not removed) per client request.
                                <div className="ms-events-filter-item">
                                    <label>{eventsListing?.categoryLabel}</label>
                                    <NiceSelect
                                        options={buildOptions(categoryOptions, eventsListing?.allCategories)}
                                        defaultCurrent={0}
                                        onChange={handleFilterChange}
                                        name="category"
                                        className="ms-nice-select"
                                    />
                                </div>
                                */}
                                <div className="ms-events-filter-item">
                                    <label>{eventsListing?.genreLabel}</label>
                                    <NiceSelect
                                        options={buildOptions(filterOptions.genres, eventsListing?.allGenres)}
                                        defaultCurrent={0}
                                        onChange={handleFilterChange}
                                        name="genre"
                                        className="ms-nice-select"
                                    />
                                </div>
                                <div className="ms-events-filter-item">
                                    <label>{eventsListing?.countryLabel}</label>
                                    <NiceSelect
                                        options={buildOptions(countryOptions, eventsListing?.allCountries)}
                                        defaultCurrent={0}
                                        onChange={handleFilterChange}
                                        name="country"
                                        className="ms-nice-select"
                                    />
                                </div>
                                <div className="ms-events-filter-item">
                                    <label>{eventsListing?.cityLabel}</label>
                                    <NiceSelect
                                        options={buildOptions(filterOptions.cities, eventsListing?.allCities)}
                                        defaultCurrent={0}
                                        onChange={handleFilterChange}
                                        name="city"
                                        className="ms-nice-select"
                                    />
                                </div>
                                <div className="ms-events-filter-item">
                                    <label>{eventsListing?.venueLabel}</label>
                                    <NiceSelect
                                        options={buildOptions(filterOptions.venues, eventsListing?.allVenues)}
                                        defaultCurrent={0}
                                        onChange={handleFilterChange}
                                        name="venue"
                                        className="ms-nice-select"
                                    />
                                </div>
                                {/* Organizer filter - commented out (not removed) per client request.
                                <div className="ms-events-filter-item">
                                    <label>{eventsListing?.organizerLabel}</label>
                                    <NiceSelect
                                        options={buildOptions(filterOptions.organizers, eventsListing?.allOrganizers)}
                                        defaultCurrent={0}
                                        onChange={handleFilterChange}
                                        name="organizer"
                                        className="ms-nice-select"
                                    />
                                </div>
                                */}
                                <div className="ms-events-filter-item">
                                    <label>{eventsListing?.monthLabel}</label>
                                    <NiceSelect
                                        options={buildOptions(monthOptions, eventsListing?.allMonths)}
                                        defaultCurrent={0}
                                        onChange={handleFilterChange}
                                        name="month"
                                        className="ms-nice-select"
                                    />
                                </div>

                                {hasActiveFilters && (
                                    <button type="button" className="ms-border-btn ms-events-filter-clear" onClick={handleClearFilters}>
                                        {eventsListing?.clearFilters}
                                    </button>
                                )}
                            </div>

                            {dataLoading ? (
                                <Loader />
                            ) : events.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="section__subtitle">{eventsListing?.noEventsFound}</p>
                                </div>
                            ) : (
                                <>
                                    {isFallback && (
                                        <div className="mb-30 text-center">
                                            <p className="section__subtitle">{eventsListing?.noExactMatches}</p>
                                        </div>
                                    )}
                                    <div className="row">
                                        {events.map(item => {
                                            let imgArray = [];
                                            try {
                                                imgArray = item?.eventImage ? JSON.parse(item.eventImage) : [];
                                            } catch {
                                                imgArray = [];
                                            }
                                            const firstImg = imgArray?.[0] || "assets/img/function-brand/function-brand-01.png";
                                            const venueName = item?.eventVenuOrStadium?.name || item?.otherVenuOrStadium || "";
                                            const venueLabel = venueName ? `${venueName}${item?.city ? `, ${item.city}` : ''}` : "";
                                            const eventTimeLabel = formatEventDateTime(item?.shedule, i18n.language === 'It' ? 'it-IT' : 'en-US');

                                            return (
                                                <div className="col-xl-3 col-lg-4 col-md-6 col-12 mb-30" key={item._id}>
                                                    <div
                                                        className="ms-genres-item h-100"
                                                        onClick={() => handleEventClick(item)}
                                                        style={{ cursor: 'pointer' }}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleEventClick(item); }}
                                                    >
                                                        <div className="ms-genres-img ms-events-listing-img">
                                                            <div className="ms-events-overlay"></div>
                                                            <img src={firstImg} alt={item?.eventName} loading="lazy" />
                                                            <span className="ms-popular__link">
                                                                <span className="ms-popular-icon"><i className="fa-regular fa-arrow-right-long"></i></span>
                                                            </span>
                                                        </div>
                                                        <h4 className="ms-genres-title ms-events-title mt-3" title={item?.eventName}>
                                                            {item?.eventName}
                                                        </h4>
                                                        {venueLabel && (
                                                            <p className="ms-genres-text mb-0 ms-events-meta-row" title={venueLabel}>
                                                                <FaLocationDot />
                                                                <span className="ms-events-truncate-text">{venueLabel}</span>
                                                            </p>
                                                        )}
                                                        {eventTimeLabel && (
                                                            <p className="ms-genres-text mb-0 ms-events-meta-row">
                                                                <FaRegClock />
                                                                <span>{eventTimeLabel}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                        </div>
                    </section>
                </div>
            )}
        </>
    );
};

export default EventsListing;
